// @ts-nocheck
/**
 * パーティ管理フック
 * @module twinKey/hooks/useParty
 */

import { useState, useCallback, useMemo } from 'react';
import { useTwinKeyContext } from '../context/TwinKeyContext';
import {
  createParty,
  addMember,
  removeMember,
  swapMembers,
  getPartyStats,
  addExperience,
  createMonsterInstance,
} from '../core';

/**
 * パーティ管理フック
 * @returns {Object} パーティ操作API
 */
export function useParty() {
  const {
    state,
    actions,
    playerData,
    updatePlayerData,
    allCards,
    getCardById,
  } = useTwinKeyContext();

  // 選択中のスロット
  const [selectedSlot, setSelectedSlot] = useState(null);

  // ============================================================
  // パーティ一覧
  // ============================================================

  /**
   * 保存されたパーティ一覧を取得
   */
  const parties = useMemo(() => {
    return playerData?.twinKey?.parties || [];
  }, [playerData]);

  /**
   * アクティブパーティのID
   */
  const activePartyId = useMemo(() => {
    return playerData?.twinKey?.activePartyId || null;
  }, [playerData]);

  /**
   * モンスター成長データ
   */
  const monsterProgress = useMemo(() => {
    return playerData?.twinKey?.monsterProgress || {};
  }, [playerData]);

  // ============================================================
  // パーティ操作
  // ============================================================

  /**
   * 新しいパーティを作成
   * @param {string} name - パーティ名
   * @returns {Object} 作成されたパーティ
   */
  const createNewParty = useCallback((name = 'パーティ') => {
    const party = createParty(name);

    // state.activeParty を更新
    actions.setActiveParty(party);

    // playerData にも保存
    updatePlayerData?.((prev) => ({
      ...prev,
      twinKey: {
        ...prev.twinKey,
        parties: [...(prev.twinKey?.parties || []), party],
        activePartyId: party.id,
      },
    }));

    return party;
  }, [actions, updatePlayerData]);

  /**
   * パーティを削除
   * @param {string} partyId - パーティID
   */
  const deleteParty = useCallback((partyId) => {
    updatePlayerData?.((prev) => {
      const newParties = (prev.twinKey?.parties || []).filter(p => p.id !== partyId);
      const newActiveId = prev.twinKey?.activePartyId === partyId
        ? (newParties[0]?.id || null)
        : prev.twinKey?.activePartyId;

      return {
        ...prev,
        twinKey: {
          ...prev.twinKey,
          parties: newParties,
          activePartyId: newActiveId,
        },
      };
    });
  }, [updatePlayerData]);

  /**
   * パーティを選択
   * @param {string} partyId - パーティID
   */
  const selectParty = useCallback((partyId) => {
    const party = parties.find(p => p.id === partyId);
    if (party) {
      actions.setActiveParty(party);
      updatePlayerData?.((prev) => ({
        ...prev,
        twinKey: {
          ...prev.twinKey,
          activePartyId: partyId,
        },
      }));
    }
  }, [parties, actions, updatePlayerData]);

  // ============================================================
  // メンバー操作
  // ============================================================

  /**
   * ストレージのパーティを更新
   */
  const updatePartyInStorage = useCallback((party) => {
    updatePlayerData?.((prev) => ({
      ...prev,
      twinKey: {
        ...prev.twinKey,
        parties: (prev.twinKey?.parties || []).map(p =>
          p.id === party.id ? party : p
        ),
      },
    }));
  }, [updatePlayerData]);

  /**
   * メンバーを追加
   * @param {Object} card - カード情報（idプロパティを持つ）
   * @param {number} slot - スロット番号（0-4）
   */
  const addPartyMember = useCallback((card, slot = null) => {
    if (!state.activeParty) {
      console.warn('addPartyMember: activeParty is null');
      return { success: false, reason: 'パーティが選択されていません' };
    }

    // カードIDを取得（card.id または card.cardId をサポート）
    const cardId = card.id || card.cardId;
    if (!cardId) {
      console.warn('addPartyMember: card has no id', card);
      return { success: false, reason: 'カードIDがありません' };
    }

    // 成長データを取得または作成
    const progress = monsterProgress[cardId] || null;
    const cardData = getCardById(cardId);

    if (!cardData) {
      console.warn('addPartyMember: card not found', cardId);
      return { success: false, reason: 'カードが見つかりません' };
    }

    // 元のカードデータに、渡されたプロパティをマージ（uniqueId, uniqueKey, rarity, level等を保持）
    const enrichedCard = {
      ...cardData,
      ...card,
    };

    const result = addMember(state.activeParty, enrichedCard, progress);

    if (result.success) {
      actions.setActiveParty(result.party);
      updatePartyInStorage(result.party);
    }

    return result;
  }, [state.activeParty, monsterProgress, getCardById, actions, updatePartyInStorage]);

  /**
   * メンバーを削除
   * @param {number} slot - スロット番号
   */
  const removePartyMember = useCallback((slot) => {
    if (!state.activeParty) return;

    // slotからuniqueIdを取得
    const member = state.activeParty.members[slot];
    if (!member) return;

    const result = removeMember(state.activeParty, member.uniqueId);
    actions.setActiveParty(result);
    updatePartyInStorage(result);
  }, [state.activeParty, actions, updatePartyInStorage]);

  /**
   * メンバーを入れ替え
   * @param {number} slot1 - スロット1
   * @param {number} slot2 - スロット2
   */
  const swapPartyMembers = useCallback((slot1, slot2) => {
    if (!state.activeParty) return;

    const result = swapMembers(state.activeParty, slot1, slot2);
    actions.setActiveParty(result);
    updatePartyInStorage(result);
  }, [state.activeParty, actions, updatePartyInStorage]);

  // ============================================================
  // モンスター成長
  // ============================================================

  /**
   * 経験値を追加
   * @param {string} cardId - カードID
   * @param {number} exp - 経験値
   */
  const addMonsterExp = useCallback((cardId, exp) => {
    updatePlayerData?.((prev) => {
      const progress = prev.twinKey?.monsterProgress || {};
      const current = progress[cardId] || { level: 1, exp: 0, awakening: 0 };

      // カードのレアリティを取得
      const collectionCard = prev.collection?.find(c => c.cardId === cardId);
      const rarity = collectionCard?.rarity || 'C';

      const updated = addExperience(current, exp, rarity);

      return {
        ...prev,
        twinKey: {
          ...prev.twinKey,
          monsterProgress: {
            ...progress,
            [cardId]: updated,
          },
        },
      };
    });
  }, [updatePlayerData]);

  /**
   * パーティ全体に経験値を分配
   * @param {number} totalExp - 総経験値
   */
  const distributeExp = useCallback((totalExp) => {
    if (!state.activeParty) return;

    const members = state.activeParty.members.filter(m => m !== null);
    if (members.length === 0) return;

    const expPerMember = Math.floor(totalExp / members.length);

    for (const member of members) {
      addMonsterExp(member.cardId, expPerMember);
    }
  }, [state.activeParty, addMonsterExp]);

  // ============================================================
  // ヘルパー
  // ============================================================

  /**
   * コレクションからパーティに追加可能なモンスターを取得
   */
  const availableMonsters = useMemo(() => {
    if (!playerData?.collection || !allCards) return [];

    // コレクションは配列形式: [{ cardId, rarity, quantity }, ...]
    return playerData.collection
      .filter(c => c.quantity > 0)
      .map(collectionCard => {
        const cardData = allCards.find(card => card.id === collectionCard.cardId);
        if (!cardData || cardData.type !== 'monster') return null;

        return {
          ...collectionCard,
          ...cardData,
          progress: monsterProgress[collectionCard.cardId] || null,
        };
      })
      .filter(Boolean);
  }, [playerData, allCards, monsterProgress]);

  /**
   * パーティの合計ステータスを取得
   */
  const partyStats = useMemo(() => {
    if (!state.activeParty) return null;
    return getPartyStats(state.activeParty);
  }, [state.activeParty]);

  // ============================================================
  // 返却値
  // ============================================================

  return {
    // 状態
    activeParty: state.activeParty,
    parties,
    activePartyId,
    monsterProgress,
    selectedSlot,
    setSelectedSlot,

    // パーティ操作
    createNewParty,
    deleteParty,
    selectParty,

    // メンバー操作
    addPartyMember,
    removePartyMember,
    swapPartyMembers,

    // 成長
    addMonsterExp,
    distributeExp,

    // ヘルパー
    availableMonsters,
    partyStats,
  };
}

export default useParty;
